import { cn } from '../../lib/utils';

interface SectionShellProps {
  /** Section id used for the in-page anchor + scroll-spy targeting. */
  readonly id: string;
  /**
   * `default` = white background.
   * `alt` = surface-gray background that bleeds full-viewport behind the
   * sticky rail via the `::before` -9999px trick. Mirrors the prototype's
   * `.section.section-alt` pattern (`_archive/home.html` lines 422–435).
   */
  readonly variant?: 'default' | 'alt';
  readonly children: React.ReactNode;
}

/**
 * Editorial-section wrapper used by all 4 homepage body sections. Holds the
 * shared `padding: 80px 56px` (mobile keeps `px-6`) and — when `variant="alt"`
 * — the full-bleed gray background hack so the colored band extends past
 * the constrained grid column to the viewport edge.
 *
 * Component intentionally has zero internal max-width or alignment opinion —
 * the section header and content shape themselves.
 *
 * BUG-003 (2026-06-01): The alt-variant `::before` bleed previously used an
 * unconditional `-left-[9999px] -right-[9999px]`. On mobile the section
 * already spans the full viewport (single-column grid), so the 9999px
 * extension produced a wide blank horizontal scroll area to the right of
 * the page (Maria's Slack report). Fix: keep `inset-x-0` on mobile (bleed
 * matches section = matches viewport, no overflow) and only apply the
 * `-9999px` extension at `lg` and up, where the section sits inside the
 * 9/12 grid column and the bleed is actually needed to reach the viewport
 * edge.
 */
export function SectionShell({ id, variant = 'default', children }: SectionShellProps) {
  return (
    <section
      id={id}
      className={cn(
        'px-6 py-20 md:px-14',
        variant === 'alt'
          ? // `relative isolate` creates a stacking context so the ::before
            // sits behind THIS section's content but above the section
            // before it. On mobile the pseudo matches the section bounds
            // (inset-x-0); on lg+ it extends 9999px each side to bleed
            // past the constrained 9/12 grid column to the viewport edge.
            [
              'relative isolate',
              'before:absolute before:inset-y-0 before:inset-x-0 before:bg-surface before:-z-10',
              'lg:before:-left-[9999px] lg:before:-right-[9999px]',
            ].join(' ')
          : 'bg-white',
      )}
    >
      {children}
    </section>
  );
}
